/* global jQuery */
'use strict';

const csrf = require('public/js/lib/csrf/utils');
const localStorageSlim = require('localstorage-slim');
const utils = require('public/js/helpers/utils');
const { notify, NotificationType } = require('public/js/lib/notify');
const { CSRF_TOKEN_SCOPE_CACHE_KEY, isCsrfTokenCacheKey } = require('public/js/lib/csrf/token-fetcher');
const CSRF_FAILURE_ERROR_CODE = 'csrf_invalid';
const CSRF_FAILURE_ERROR_HEADER = 'X-RP-Error-Code';
const CSRF_TIMEOUT_NOTIFICATION_ID = 'rp-csrf-timeout-dialog';

const CSRF_TIMEOUT_TRANSLATIONS = {
	title: { key: 'csrf_timeout_title', fallback: 'Your session has timed out.' },
	message: { key: 'csrf_timeout_message', fallback: 'We\'ve saved your progress. Please refresh the page to continue shopping.' },
	action: { key: 'csrf_timeout_action', fallback: 'Refresh Page' },
};

/**
 * @param {keyof CSRF_TIMEOUT_TRANSLATIONS} field
 * @returns {string}
 */
function getCsrfTimeoutText(field) {
	const { key, fallback } = CSRF_TIMEOUT_TRANSLATIONS[field];
	return globalThis.javascriptContent?.[key] || fallback;
}

/**
 * @returns {void}
 */
function notifyCsrfTimeout() {
	clearCachedCsrfTokens();
	notify.add_notification({
		action: {
			label: getCsrfTimeoutText('action'),
			onClick: () => {
				const appReload = globalThis?.rp_app?.location?.reload;
				if (typeof appReload === 'function') {
					appReload.call(globalThis.rp_app.location);
					return;
				}

				if (typeof globalThis?.location?.reload === 'function') {
					globalThis.location.reload();
				}
			},
		},
		id: CSRF_TIMEOUT_NOTIFICATION_ID,
		message: getCsrfTimeoutText('message'),
		title: getCsrfTimeoutText('title'),
		type: NotificationType.ERROR,
		delay: -1
	});
}

/**
 * Clears all known CSRF localStorage cache keys.
 * @returns {void}
 */
function clearCachedCsrfTokens() {
	if (!globalThis?.localStorage || typeof globalThis.localStorage.length !== 'number') {
		return;
	}

	for (let index = globalThis.localStorage.length - 1; index >= 0; index -= 1) {
		const key = globalThis.localStorage.key(index);
		if (isCsrfTokenCacheKey(key)) {
			localStorageSlim.remove(key);
		}
	}

	localStorageSlim.remove(CSRF_TOKEN_SCOPE_CACHE_KEY);
}

/**
 * Combines the page-load gates into one promise that settles once all of them have.
 *
 * Rejections are absorbed so a failed gate still releases the burst; only the caller's timeout
 * rejects the combined promise.
 *
 * @param {Array<unknown>} gates
 * @returns {Promise|null} null when no gate is installed, so callers can skip deferral entirely.
 */
function combineReadyGates(gates) {
	const installed = gates.filter((gate) => gate && typeof gate.then === 'function');
	if (installed.length === 0) {
		return null;
	}

	// Returned as-is rather than wrapped, so a lone gate releases requests on the same tick it
	// settles instead of the extra one Promise.all would cost.
	if (installed.length === 1) {
		return installed[0];
	}

	const settle = () => {};

	return Promise.all(installed.map((gate) => gate.then(settle, settle)));
}

/**
 * Wraps window.$ajax so that all same-origin requests (except gate-settling
 * endpoints) are deferred until the page-load gates settle. This prevents
 * pre-bootstrap requests from racing the token fetch and clobbering the
 * session, and prevents the page-load burst from overwriting the fulfillment
 * hash or acquisition source those gates commit. Cross-origin requests, the
 * exempt endpoints, and all requests after settlement pass through to the
 * original $ajax.
 *
 * Bots skip this wrapper entirely: the token fetch is already a no-op for
 * them (see token-fetcher), so deferring would just stall their requests.
 * @param {function(Promise): Promise} raceWithTimeout
 * @returns {void}
 */
function installAjaxDeferralWrapper(raceWithTimeout) {
	try {
		if (typeof globalThis.$ajax !== 'function' || globalThis.$ajax.__rp_csrf_wrapped) {
			return;
		}

		if (utils.isBot()) {
			return;
		}

		const original$ajax = globalThis.$ajax;
		let settled = false;

		const ready = combineReadyGates([
			globalThis.__rp_csrf_ready_promise,
			globalThis.__rp_session_attribution_ready_promise,
			globalThis.__rp_supplier_ready_promise,
		]);
		const readyWithTimeout = ready === null ? null : raceWithTimeout(ready);

		if (ready !== null) {
			ready.then(function () { settled = true; }, function () { settled = true; });
		}
		if (readyWithTimeout !== null) {
			readyWithTimeout.then(null, function () { settled = true; });
		}

		globalThis.$ajax = function rpCsrfAjaxWrapper() {
			const args = arguments;
			const opts = args[0];

			if (!settled && readyWithTimeout !== null && csrf.shouldDeferRequest(opts)) {
				const jqDeferred = jQuery.Deferred();
				let realJqXHR = null;
				let aborted = false;
				let pendingStatusCodes = null;

				const proxy = jqDeferred.promise();

				proxy.abort = function (statusText) {
					if (realJqXHR) {
						realJqXHR.abort(statusText);
						return;
					}
					aborted = true;
					jqDeferred.reject(proxy, statusText || 'abort');
				};

				proxy.statusCode = function (map) {
					if (realJqXHR) {
						realJqXHR.statusCode(map);
					} else if (map) {
						pendingStatusCodes = Object.assign(pendingStatusCodes || {}, map);
					}
					return proxy;
				};

				const fire = () => {
					if (aborted) return;
					realJqXHR = original$ajax.apply(this, args);
					if (pendingStatusCodes) realJqXHR.statusCode(pendingStatusCodes);
					realJqXHR.done(jqDeferred.resolve).fail(jqDeferred.reject);
				};

				readyWithTimeout.then(fire, fire);
				return proxy;
			}

			return original$ajax.apply(this, args);
		};

		globalThis.$ajax.__rp_csrf_wrapped = true;
	} catch (e) {
		console.warn('[csrf-bootstrap] installAjaxDeferralWrapper failed', e); // eslint-disable-line no-console
	}
}

/**
 * Installs a namespaced ajaxSend hook onto the provided jQuery instance.
 * @param {unknown} jq - Candidate jQuery instance.
 * @returns {void}
 */
function installCsrfAjaxHook(jq) {
	try {
		if (typeof jq !== 'function' || jq.__rp_csrf_ajax_hook_installed) {
			return;
		}

		const jqDocument = jq(document);
		if (!jqDocument || typeof jqDocument.on !== 'function') {
			return;
		}

		jqDocument.on('ajaxSend.rpCsrfBootstrap', (_event, jqxhr, ajaxOptions) => {
			if (!csrf.shouldAttachCsrfHeader(ajaxOptions) || !jqxhr || typeof jqxhr.setRequestHeader !== 'function') {
				return;
			}

			const csrfConfig = csrf.getCsrfConfig();
			const existingHeaders = csrf.normalizeHeaders(ajaxOptions.headers);
			if (csrf.hasHeader(existingHeaders, csrfConfig.header_name.toLowerCase())) {
				return;
			}

			const token = csrf.getCsrfTokenFromMeta() || csrf.getToken();
			if (!token) {
				return;
			}

			jqxhr.setRequestHeader(csrfConfig.header_name, token);
		});

		jqDocument.on('ajaxError.rpCsrfBootstrap', (_event, jqxhr) => {
			const status = Number(jqxhr?.status || 0);
			if (status !== 403 || !jqxhr || typeof jqxhr.getResponseHeader !== 'function') {
				return;
			}

			const responseErrorCode = String(jqxhr.getResponseHeader(CSRF_FAILURE_ERROR_HEADER) || '').toLowerCase();
			if (responseErrorCode !== CSRF_FAILURE_ERROR_CODE) {
				return;
			}
			notifyCsrfTimeout();
		});

		jq.__rp_csrf_ajax_hook_installed = true;
	} catch (e) {
		console.warn('[csrf-bootstrap] installCsrfAjaxHook failed', e); // eslint-disable-line no-console
	}
}

/**
 * Installs CSRF ajax hooks for known jQuery instances.
 * @returns {void}
 */
function installCsrfAjaxHooks() {
	try {
		const candidates = [globalThis.jQuery, globalThis.rp$];
		const seen = new Set();

		for (const candidate of candidates) {
			if (!candidate || seen.has(candidate)) {
				continue;
			}
			seen.add(candidate);
			installCsrfAjaxHook(candidate);
		}
	} catch (e) {
		console.warn('[csrf-bootstrap] installCsrfAjaxHooks failed', e); // eslint-disable-line no-console
	}
}

module.exports = {
	CSRF_FAILURE_ERROR_CODE,
	CSRF_FAILURE_ERROR_HEADER,
	clearCachedCsrfTokens,
	installAjaxDeferralWrapper,
	installCsrfAjaxHooks,
	notifyCsrfTimeout,
};
